import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { toast } from 'react-toastify';
import jwt_decode from 'jwt-decode';
// Components
import CustomButton from 'components/common/CustomButton';
import LoadingOverlay from 'components/common/LoadingOverlay';
import SearchIcon from 'assets/images/svg/SearchIcon';
// Helpers
import { EXPERIENCE_LEVELS, OPS_LEVELS } from 'constants/OrdersFilters';
// API
import OrdersListService from 'services/OrdersListService';
import FloristAssignmentService from 'services/FloristAssignmentService';
// Localstorage
import { getFromLocalStorage } from 'helpers/LocalStorage';

const SEARCH_DEBOUNCE = 400;

/**
 * onAssignToMe / onAssignToFlorist let a caller swap in its own assignment call - the
 * dropped orders list reassigns through the assignment service instead.
 */
const AssignOrderModal = ({
  isModalOpen,
  setIsOpenModal,
  orderData,
  fetchData,
  onAssignToMe,
  onAssignToFlorist,
}) => {
  const [florists, setFlorists] = useState([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const [selectedFlorist, setSelectedFlorist] = useState(-1);
  const [isLoaderOpen, setIsloaderOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const selectedName = florists?.find((f) => f?.userId === selectedFlorist)?.name;

  // Hold off on the request until typing settles.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), SEARCH_DEBOUNCE);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const fetchFlorists = async () => {
      setIsListLoading(true);
      try {
        const res = await FloristAssignmentService.getFlorists({
          opsLevel: OPS_LEVELS.ALL,
          ...(search ? { search } : {}),
        });
        // The endpoint answers with a bare array, not the paged envelope.
        const payload = res?.data;
        setFlorists((Array.isArray(payload) ? payload : payload?.data) ?? []);
      } catch (err) {
        setFlorists([]);
        toast.error(err?.response?.data?.message ?? 'Could not load the florists list');
      } finally {
        setIsListLoading(false);
      }
    };
    fetchFlorists();
  }, [search]);

  const handleAssignToMe = async () => {
    if (onAssignToMe) {
      setIsOpenModal(false);
      onAssignToMe();
      return;
    }
    setIsloaderOpen(true);
    try {
      await OrdersListService.assignFloristStatus(
        {
          assigneeId:
            getFromLocalStorage('floristId') ?? jwt_decode(getFromLocalStorage('token'))?.id,
        },
        orderData?.id
      );
      setIsOpenModal(false);
      setTimeout(() => fetchData(), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Status change failed!');
      setIsloaderOpen(false);
    }
  };

  const handleAssignToOther = async () => {
    if (onAssignToFlorist) {
      setIsOpenModal(false);
      onAssignToFlorist(selectedFlorist);
      return;
    }
    setIsloaderOpen(true);
    try {
      await OrdersListService.assignFloristStatus({ assigneeId: selectedFlorist }, orderData?.id);
      setIsOpenModal(false);
      setTimeout(() => fetchData(), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Status change failed!');
      setIsloaderOpen(false);
    }
  };

  return (
    <>
      {isLoaderOpen && <LoadingOverlay />}
      <Modal
        className="assign-modal assign-modal-revamp"
        isOpen={isModalOpen}
        toggle={() => setIsOpenModal((prev) => !prev)}
        centered={true}
      >
        <ModalHeader>
          Assign order <span className="order-number">"{orderData?.id}"</span>
        </ModalHeader>
        <ModalBody>
          <form className="assign-search" onSubmit={(e) => e.preventDefault()}>
            <SearchIcon />
            <input
              id="assign-search-input"
              placeholder="Search by florist name"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button type="button" className="assign-search-clear" onClick={() => setSearchText('')}>
                &times;
              </button>
            )}
          </form>

          <p className="assign-intro">
            Select a florist to assign this order to. Offline florists can be assigned too -
            their status is shown for reference.
          </p>

          <div className="assign-florists">
            <div className="assign-florist-head">
              <span>Florist</span>
              <span>Skill level</span>
              <span>Status</span>
              <span>Active orders</span>
            </div>

            {florists?.length > 0 ? (
              florists.map((florist) => {
                const isOffline = !florist?.isOnline;
                return (
                  <button
                    type="button"
                    key={florist?.userId}
                    className={`assign-florist-row${
                      selectedFlorist === florist?.userId ? ' selected' : ''
                    }${isOffline ? ' offline' : ''}`}
                    onClick={() => setSelectedFlorist(florist?.userId)}
                  >
                    <span className="assign-florist-main">
                      <span className="assign-radio" />
                      <span className="assign-florist-name">{florist?.name}</span>
                    </span>

                    <span className="assign-skill">
                      {EXPERIENCE_LEVELS[florist?.experienceLevel] ?? '-'}
                    </span>

                    <span className="assign-status">
                      <span className="assign-status-dot" />
                      {florist?.isOnline ? 'Online' : 'Offline'}
                    </span>

                    <span
                      className="assign-florist-count"
                      title={`${florist?.activeOrders ?? 0} of ${florist?.maxActiveOrders ?? 0}`}
                    >
                      {florist?.activeOrders ?? 0}
                    </span>
                  </button>
                );
              })
            ) : (
              <p className="assign-empty">
                {isListLoading ? 'Loading florists…' : 'No florists found'}
              </p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="assign-footer">
            <CustomButton
              id="modal-assign-me-btn"
              name="Assign to Myself"
              handleClick={() => handleAssignToMe()}
              disabled={isLoaderOpen}
              type="assign-secondary"
            />
            <div className="assign-footer-right">
              <CustomButton
                id="modal-cancel-btn"
                name="Cancel"
                handleClick={() => setIsOpenModal(false)}
                disabled={false}
                type="assign-secondary"
              />
              <CustomButton
                id="modal-assign-btn"
                name={selectedName ? `Assign to ${selectedName}` : 'Assign'}
                handleClick={() => handleAssignToOther()}
                disabled={isLoaderOpen || selectedFlorist === -1}
                type="assign-primary"
              />
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AssignOrderModal;
